/**
 * Phase 117B — PWA install context.
 *
 * Captures the browser's `beforeinstallprompt` event (Chrome/Edge/Brave
 * on desktop + Android) and exposes it through a React context so any
 * component — most importantly the brand logo in both Out-Play sidebar
 * and the ArenaX wordmark — can trigger the install prompt on click.
 *
 * iOS Safari does NOT support `beforeinstallprompt`; on those devices
 * we surface a tooltip nudge instead ("Tap Share → Add to Home Screen").
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { trackPwa } from '@/lib/pwaTelemetry';

const PwaInstallContext = createContext({
  canInstall: false,
  installed: false,
  isStandalone: false,
  isIos: false,
  promptInstall: async () => false,
});

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia && window.matchMedia('(display-mode: standalone)');
  return (mql && mql.matches) || window.navigator.standalone === true;
}

function detectIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function PwaInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(detectStandalone());
  const [isIos] = useState(detectIos());

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
      // Phase 261c — fire whenever the OS confirms install, regardless of
      // whether it came from our banner, the URL-bar icon, or any other path.
      trackPwa('pwa_app_installed', { source: 'native' });
    };
    const onDisplayModeChange = (e) => {
      setIsStandalone(e.matches);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    const mql = window.matchMedia && window.matchMedia('(display-mode: standalone)');
    mql && mql.addEventListener && mql.addEventListener('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
      mql && mql.removeEventListener && mql.removeEventListener('change', onDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setCanInstall(false);
      if (choice && choice.outcome === 'accepted') {
        setInstalled(true);
        return true;
      }
      return false;
    } catch (_e) {
      setDeferredPrompt(null);
      setCanInstall(false);
      return false;
    }
  }, [deferredPrompt]);

  return (
    <PwaInstallContext.Provider value={{
      canInstall,
      installed,
      isStandalone,
      isIos,
      promptInstall,
    }}>
      {children}
    </PwaInstallContext.Provider>
  );
}

export function usePwaInstall() {
  return useContext(PwaInstallContext);
}
